import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast, ToastContainer } from 'react-toastify';
import Navbar from '../../components/layout/NavBar';
import Footer from '../../components/layout/Footer';
import { useAuth } from '../../context/AuthContext';
import { getProperty, getPropertyImages, startConversation, createVisit, getPropertyTour } from '../../services/api';
import TourViewer from '../tours/TourViewer';
import { useCompare } from '../../context/CompareContext';
import { FiArrowLeft, FiMessageSquare, FiBarChart2, FiMapPin, FiHome, FiStar, FiShare2, FiCalendar, FiCheckCircle } from 'react-icons/fi';
import { IoBedOutline } from 'react-icons/io5';
import { LuBath, LuRuler } from 'react-icons/lu';
import { HiOutlineHeart, HiHeart } from 'react-icons/hi';

import { API_URL } from '../../services/api';

const formatCOP = (v) => new Intl.NumberFormat('es-CO', {
    style: 'currency', currency: 'COP', maximumFractionDigits: 0
}).format(v);

const imgSrc = (url) => url ? (url.startsWith('/') ? `${API_URL}${url}` : url) : null;

const PropertyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const { addToCompare, removeFromCompare, isComparing } = useCompare();

    const [propiedad, setPropiedad] = useState(null);
    const [images, setImages] = useState([]);
    const [activeImg, setActiveImg] = useState(0);
    const [loading, setLoading] = useState(true);
    const [contacting, setContacting] = useState(false);

    // Visit Modal state
    const [showVisitModal, setShowVisitModal] = useState(false);
    const [visitDate, setVisitDate] = useState('');
    const [visitTime, setVisitTime] = useState('');
    const [visitNotes, setVisitNotes] = useState('');
    const [schedulingVisit, setSchedulingVisit] = useState(false);
    const [savedFav, setSavedFav] = useState(false);
    const [tour, setTour] = useState(null);
    const [activeTab, setActiveTab] = useState('fotos'); // 'fotos' | 'tour'

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const [propData, imgData] = await Promise.all([
                    getProperty(id),
                    getPropertyImages(id),
                ]);
                setPropiedad(propData);
                // Build the full image list: main image first, then gallery images
                const allImgs = [];
                if (propData.imagen_url) allImgs.push({ url: propData.imagen_url, id: 'main' });
                imgData.forEach(img => {
                    if (!allImgs.find(i => i.url === img.url)) allImgs.push(img);
                });
                setImages(allImgs);
                try {
                    const tourData = await getPropertyTour(id);
                    if (tourData.tour) setTour(tourData.tour);
                } catch {
                    // tour is optional — ignore errors
                }
            } catch (err) {
                toast.error('Error al cargar la propiedad');
                setTimeout(() => navigate('/'), 2000);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [id, navigate]);

    const handleContact = async () => {
        if (!isAuthenticated) return navigate('/login');
        if (String(user.id) === String(propiedad.propietario_id || propiedad.propietario)) return navigate('/my-properties');
        setContacting(true);
        try {
            const data = await startConversation(id, user.id, propiedad.propietario_id);
            navigate('/messages', { state: { openConversationId: data.id } });
        } catch (e) {
            toast.error('Error al iniciar la conversación');
        } finally {
            setContacting(false);
        }
    };

    const handleScheduleVisit = async () => {
        if (!isAuthenticated) {
            toast.info('Debes iniciar sesión para agendar una visita');
            return navigate('/login');
        }
        if (!visitDate || !visitTime) {
            return toast.warning('Debes seleccionar fecha y hora para la visita');
        }

        setSchedulingVisit(true);
        try {
            await createVisit({
                propiedad_id: propiedad.id,
                arrendador_id: propiedad.propietario_id,
                arrendatario_id: user.id,
                fecha_hora: `${visitDate}T${visitTime}:00`,
                notas: visitNotes
            });
            toast.success('¡Visita solicitada con éxito! El propietario te confirmará pronto.');
            setShowVisitModal(false);
            setVisitDate('');
            setVisitTime('');
            setVisitNotes('');
        } catch (e) {
            toast.error(e.message || 'Error al agendar la visita');
        } finally {
            setSchedulingVisit(false);
        }
    };

    const toggleCompare = () => {
        if (isComparing(propiedad.id)) {
            removeFromCompare(propiedad.id);
        } else {
            addToCompare(propiedad);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-surface-50 dark:bg-surface-950">
                <Navbar />
                <div className="max-w-6xl mx-auto px-4 pt-24">
                    <div className="h-96 skeleton rounded-2xl mb-6"></div>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="md:col-span-2 h-64 skeleton rounded-2xl"></div>
                        <div className="h-64 skeleton rounded-2xl"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!propiedad) return null;

    const comparing = isComparing(propiedad?.id);

    return (
        <div className="min-h-screen bg-surface-50 dark:bg-surface-950">
            <Helmet>
                <title>{propiedad.titulo} — Arriendo en {propiedad.ciudad || 'Colombia'} | Hoommy</title>
                <meta name="description" content={`${propiedad.titulo} en ${propiedad.ciudad || 'Colombia'}. ${propiedad.tipo || 'Propiedad'} en arriendo por ${formatCOP(propiedad.precio || 0)}/mes. ${propiedad.habitaciones || 0} hab, ${propiedad.banos || 0} baños, ${propiedad.area_m2 || 0}m².`} />
                <link rel="canonical" href={`https://www.myhoommy.com/property/${propiedad.id}`} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={`${propiedad.titulo} — Arriendo en ${propiedad.ciudad || 'Colombia'}`} />
                <meta property="og:url" content={`https://www.myhoommy.com/property/${propiedad.id}`} />
                <meta property="og:image" content={images.length > 0 ? imgSrc(images[0]?.url) : 'https://www.myhoommy.com/logoHoommy.png'} />
                <meta property="og:site_name" content="Hoommy" />
                <meta name="twitter:card" content="summary_large_image" />
                <script type="application/ld+json">{JSON.stringify({
                    "@context": "https://schema.org", "@type": "RealEstateListing",
                    "name": propiedad.titulo, "description": propiedad.descripcion || '',
                    "url": `https://www.myhoommy.com/property/${propiedad.id}`,
                    "image": images.length > 0 ? imgSrc(images[0]?.url) : undefined,
                    "address": { "@type": "PostalAddress", "addressLocality": propiedad.ciudad || '', "addressCountry": "CO" },
                    "offers": { "@type": "Offer", "price": propiedad.precio || 0, "priceCurrency": "COP" }
                })}</script>
            </Helmet>
            <Navbar />
            <ToastContainer />

            <main className="max-w-6xl mx-auto px-4 pt-6 pb-16">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-1.5 text-xs text-surface-500 dark:text-surface-400 mb-5 flex-wrap">
                    <button onClick={() => navigate(-1)} className="flex items-center gap-1 hover:text-brand-600 transition-colors">
                        <FiArrowLeft className="w-3.5 h-3.5" /> Volver
                    </button>
                    <span>/</span>
                    <Link to="/" className="hover:text-brand-600 transition-colors">Inicio</Link>
                    {propiedad.ciudad && (<><span>/</span><span className="capitalize">{propiedad.ciudad}</span></>)}
                    <span>/</span>
                    <span className="text-surface-700 dark:text-surface-300 font-medium line-clamp-1 max-w-xs">{propiedad.titulo}</span>
                </nav>

                {/* ── CSS Grid Gallery ─────────────────────────── */}
                <div className="mb-8">
                    {/* Tabs fotos / tour */}
                    {tour && (
                        <div className="flex border-b border-gray-200 mb-4">
                            <button
                                onClick={() => setActiveTab('fotos')}
                                className={`px-4 py-2 text-sm font-medium ${activeTab === 'fotos' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Fotos
                            </button>
                            <button
                                onClick={() => setActiveTab('tour')}
                                className={`px-4 py-2 text-sm font-medium ${activeTab === 'tour' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Tour 360° ✦
                            </button>
                        </div>
                    )}

                    {activeTab === 'fotos' && (
                        images.length > 0 ? (
                            <div className="relative">
                                {/* Grid gallery: 1 large + up to 4 small */}
                                <div className={`grid gap-2 rounded-2xl overflow-hidden ${images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}
                                    style={{ gridTemplateRows: images.length > 1 ? '240px 240px' : '480px' }}>
                                    {/* Main image spans full height on left */}
                                    <div
                                        className="relative cursor-pointer overflow-hidden row-span-2"
                                        onClick={() => setActiveImg(0)}
                                    >
                                        <img
                                            src={imgSrc(images[0]?.url)}
                                            alt={propiedad.titulo}
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                                            fetchpriority="high"
                                            decoding="sync"
                                        />
                                    </div>
                                    {/* Side thumbnails 2×2 */}
                                    {images.slice(1, 5).map((img, i) => (
                                        <div
                                            key={i}
                                            className="relative cursor-pointer overflow-hidden"
                                            onClick={() => setActiveImg(i + 1)}
                                        >
                                            <img
                                                src={imgSrc(img.url)}
                                                alt={`Foto ${i + 2}`}
                                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                                                loading="lazy"
                                            />
                                            {/* "Ver todas" overlay on last visible thumb if there are more */}
                                            {i === 3 && images.length > 5 && (
                                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                    <span className="text-white font-bold text-sm">+{images.length - 5} fotos</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Action buttons top-right */}
                                <div className="absolute top-3 right-3 flex gap-2">
                                    <button
                                        onClick={() => { const url = window.location.href; navigator.share ? navigator.share({ title: propiedad.titulo, url }) : navigator.clipboard.writeText(url); }}
                                        className="flex items-center gap-1.5 bg-white/90 dark:bg-surface-900/90 backdrop-blur-sm text-surface-700 dark:text-surface-300 text-xs font-medium px-3 py-2 rounded-xl shadow hover:bg-white dark:hover:bg-surface-800 transition-all"
                                    >
                                        <FiShare2 className="w-3.5 h-3.5" /> Compartir
                                    </button>
                                    <button
                                        onClick={() => setSavedFav(!savedFav)}
                                        className={`flex items-center gap-1.5 backdrop-blur-sm text-xs font-medium px-3 py-2 rounded-xl shadow transition-all ${savedFav ? 'bg-rose-500 text-white' : 'bg-white/90 dark:bg-surface-900/90 text-surface-700 dark:text-surface-300 hover:bg-white dark:hover:bg-surface-800'}`}
                                    >
                                        {savedFav ? <HiHeart className="w-3.5 h-3.5" /> : <HiOutlineHeart className="w-3.5 h-3.5" />}
                                        Guardar
                                    </button>
                                </div>

                                {/* Lightbox carousel (shown when activeImg > 0 or full-screen desired) */}
                                {images.length > 1 && (
                                    <div className="mt-2 flex gap-1.5 overflow-x-auto hide-scrollbar pb-1">
                                        {images.map((img, i) => (
                                            <button key={i} onClick={() => setActiveImg(i)}
                                                className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === activeImg ? 'border-brand-500' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                                                <img src={imgSrc(img.url)} alt="" className="w-full h-full object-cover" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="h-80 rounded-2xl bg-surface-200 dark:bg-surface-800 flex items-center justify-center">
                                <FiHome className="w-16 h-16 text-surface-400" />
                            </div>
                        )
                    )}

                    {activeTab === 'tour' && tour && (
                        <div className="mb-6">
                            <TourViewer tour={tour} />
                        </div>
                    )}
                </div>

                {/* ── Two-column layout ────────────────────────── */}
                <div className="grid md:grid-cols-3 gap-8">

                    {/* Left: Info */}
                    <div className="md:col-span-2 space-y-6">

                        {/* Header card */}
                        <div className="card p-6 md:p-8">
                            {/* Badges row */}
                            <div className="flex flex-wrap gap-2 mb-4">
                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400 capitalize">
                                    {propiedad.tipo}
                                </span>
                                {propiedad.para_estudiantes && (
                                    <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                                        🎓 Para Estudiantes
                                    </span>
                                )}
                                {propiedad.ambiente_familiar && (
                                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                                        🫂 Ambiente Familiar
                                    </span>
                                )}
                                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-surface-100 dark:bg-surface-800 text-surface-500 dark:text-surface-400">
                                    <FiCheckCircle className="w-3 h-3 text-emerald-500" /> Verificado
                                </span>
                            </div>

                            <h1 className="text-2xl md:text-3xl font-extrabold text-surface-900 dark:text-white leading-snug">
                                {propiedad.titulo}
                            </h1>
                            <p className="flex items-center gap-1.5 text-surface-500 dark:text-surface-400 text-sm mt-2">
                                <FiMapPin className="text-brand-500 flex-shrink-0" />
                                {propiedad.direccion}{propiedad.direccion && ', '}{propiedad.ciudad}
                            </p>

                            {/* Price */}
                            <div className="mt-5 pt-5 border-t border-surface-100 dark:border-surface-800 flex items-end gap-2">
                                <span className="text-4xl font-extrabold text-brand-600 dark:text-brand-400">
                                    {formatCOP(propiedad.precio)}
                                </span>
                                <span className="text-surface-400 text-base mb-1">/mes</span>
                            </div>

                            {/* Specs pills */}
                            <div className="flex flex-wrap gap-3 mt-4">
                                {propiedad.habitaciones > 0 && (
                                    <span className="flex items-center gap-1.5 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-400 text-sm font-medium px-3 py-1.5 rounded-xl">
                                        <IoBedOutline className="w-4 h-4" /> {propiedad.habitaciones} hab.
                                    </span>
                                )}
                                {propiedad.banos > 0 && (
                                    <span className="flex items-center gap-1.5 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-400 text-sm font-medium px-3 py-1.5 rounded-xl">
                                        <LuBath className="w-4 h-4" /> {propiedad.banos} baños
                                    </span>
                                )}
                                {propiedad.area_m2 && (
                                    <span className="flex items-center gap-1.5 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-400 text-sm font-medium px-3 py-1.5 rounded-xl">
                                        <LuRuler className="w-4 h-4" /> {propiedad.area_m2} m²
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        {propiedad.descripcion && (
                            <div className="card p-6">
                                <h2 className="font-bold text-surface-900 dark:text-white text-lg mb-3">Descripción</h2>
                                <p className="text-surface-600 dark:text-surface-300 text-sm leading-relaxed whitespace-pre-line">{propiedad.descripcion}</p>
                            </div>
                        )}
                    </div>

                    {/* Right: Sticky CTA Sidebar */}
                    <div>
                        <div className="card p-6 space-y-4 sticky top-24">
                            {/* Price summary */}
                            <div className="text-center pb-4 border-b border-surface-100 dark:border-surface-800">
                                <p className="text-3xl font-extrabold text-brand-600 dark:text-brand-400">{formatCOP(propiedad.precio)}</p>
                                <p className="text-surface-400 text-sm mt-0.5">por mes</p>
                            </div>

                            {/* Landlord info */}
                            {propiedad.propietario && (
                                <div className="flex items-center gap-3 bg-surface-50 dark:bg-surface-800 rounded-xl p-3">
                                    <div className="w-10 h-10 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center text-brand-600 font-bold text-sm flex-shrink-0">
                                        {propiedad.propietario.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-surface-900 dark:text-white truncate">{propiedad.propietario}</p>
                                        {propiedad.propietario_total_resenas > 0 && (
                                            <span className="flex items-center gap-1 text-xs text-surface-500">
                                                <FiStar className="text-amber-400 fill-amber-400 w-3 h-3" />
                                                {Number(propiedad.propietario_calificacion).toFixed(1)} · {propiedad.propietario_total_resenas} reseñas
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* CTA buttons */}
                            <button
                                onClick={handleContact}
                                disabled={contacting}
                                className="w-full btn-primary py-3.5 rounded-xl flex items-center justify-center gap-2 font-bold text-sm shadow-lg shadow-brand-500/20"
                            >
                                <FiMessageSquare className="w-4 h-4" />
                                {contacting ? 'Conectando...' : 'Contactar Propietario'}
                            </button>

                            <button
                                onClick={() => setShowVisitModal(true)}
                                className="w-full py-3.5 rounded-xl border-2 border-brand-500 text-brand-600 dark:text-brand-400 font-bold text-sm flex items-center justify-center gap-2 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
                            >
                                <FiCalendar className="w-4 h-4" /> Agendar Visita
                            </button>

                            <button
                                onClick={toggleCompare}
                                className={`w-full py-3 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 transition-all ${comparing
                                    ? 'bg-brand-50 border-brand-300 text-brand-700 dark:bg-brand-900/30 dark:border-brand-700 dark:text-brand-400'
                                    : 'border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800'}`}
                            >
                                <FiBarChart2 className="w-4 h-4" />
                                {comparing ? 'Quitar del comparador' : 'Comparar propiedad'}
                            </button>

                            {user && propiedad && user.id === propiedad.propietario_id && (
                                <button
                                    onClick={() => navigate(`/property/${id}/create-tour`)}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded-lg font-medium transition-colors w-full"
                                >
                                    ✦ Crear tour 360°
                                </button>
                            )}

                            <p className="text-center text-xs text-surface-400">
                                Sin compromisos · Responde en menos de 24h
                            </p>
                        </div>
                    </div>
                </div>

                {/* Visit Modal */}
                {showVisitModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <div className="bg-white dark:bg-surface-900 rounded-3xl p-6 w-full max-w-md shadow-2xl animate-fade-in-up">
                            <h3 className="text-xl font-bold text-surface-900 dark:text-white mb-1">Agendar Visita</h3>
                            <p className="text-sm text-surface-500 dark:text-surface-400 mb-6">Selecciona fecha y hora. El propietario confirmará pronto.</p>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">Día de la visita</label>
                                    <input type="date" className="w-full input-field" min={new Date().toISOString().split('T')[0]} value={visitDate} onChange={(e) => setVisitDate(e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">Hora deseada</label>
                                    <input type="time" className="w-full input-field" value={visitTime} onChange={(e) => setVisitTime(e.target.value)} />
                                    {propiedad.horarios_visita && (
                                        <p className="text-xs text-surface-400 mt-2">
                                            <strong>Disponibilidad:</strong>{' '}
                                            {typeof propiedad.horarios_visita === 'string' ? 'Ver detalles' : Object.entries(propiedad.horarios_visita).filter(([, h]) => h.activo).map(([d, h]) => (
                                                <span key={d} className="block capitalize">- {d}: {h.inicio} a {h.fin}</span>
                                            ))}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">Notas (Opcional)</label>
                                    <textarea className="w-full input-field min-h-[80px]" placeholder="Ej. Iré con mis padres..." value={visitNotes} onChange={(e) => setVisitNotes(e.target.value)} />
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-3">
                                <button onClick={() => setShowVisitModal(false)} className="px-5 py-2.5 rounded-xl font-medium text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800">
                                    Cancelar
                                </button>
                                <button onClick={handleScheduleVisit} disabled={schedulingVisit} className="px-6 py-2.5 rounded-xl btn-primary shadow-lg shadow-brand-500/30 font-semibold">
                                    {schedulingVisit ? 'Solicitando...' : 'Confirmar Visita'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
};

export default PropertyDetail;
